import { useEffect, useRef, useState } from "react";
import SubscriberLink from "../forms/SubscriberLink";
import ArchivePlansModal from "../action-plan/ArchivePlansModal";

interface ArchivePlan {
    year: string;
    title: string;
    link: string;
}

interface InsightsHeroProps {
    data: {
        id: string;
        tagline: string;
        heading: string;
        paragraph: string;
        className: string;
        downloadButton: {
            text: string;
            link: string;
        };
        archiveButton?: {
            text: string;
            tableHeaders?: {
                year: string;
                title: string;
                download: string;
            };
        };
        archivePlans?: ArchivePlan[];
        image: {
            src: string;
            alt: string;
            srcset: string;
        };
    };
}

const InsightsHero: React.FC<InsightsHeroProps> = ({ data }) => {
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);
    const imageRef = useRef<HTMLImageElement>(null);
    const rafRef = useRef<number | null>(null);

    // Cancel any pending frame when the component unmounts
    useEffect(() => {
        return () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    // Write the transform straight to the DOM so moving the mouse never re-renders the tree
    const applyTransform = (transform: string) => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            if (imageRef.current) imageRef.current.style.transform = transform;
        });
    };

    // Mouse move event handler
    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const { clientX, clientY, currentTarget } = event;
        const { offsetWidth, offsetHeight } = currentTarget;
        const xPos = (clientX / offsetWidth) * 100;
        const yPos = (clientY / offsetHeight) * 100;

        // Adjust these values to fine-tune the effect
        const translateX = (xPos - 50) / 10;
        const translateY = (yPos - 50) / 10;
        const rotateX = (yPos - 50) / 20;
        const rotateY = (xPos - 50) / 20;

        applyTransform(
            `translate3d(${translateX}px, ${translateY}px, 0) scale(1) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`
        );
    };

    // Reset transform when the mouse leaves the image area
    const handleMouseLeave = () => {
        applyTransform('translate3d(0px, 0px, 0px) scale(1) rotateX(0deg) rotateY(0deg)');
    };


    return (
        <div className="hero-section"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <section className="insights-entrance">
                <div className="w-layout-grid hero-grid">
                    <div className="hero-content-div">
                        <div className="heading-set middle">
                            <div className="limited-heading-set">
                                <div className="tagline-heading">{data.tagline}</div>
                                <div className="heading-set-3">
                                    <h1 className="heading-1">{data.heading}</h1>
                                    <div className="paragraph-large">{data.paragraph}</div>
                                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                                        {/* {data.archiveButton && data.archivePlans && data.archivePlans.length > 0 && (
                                            <a
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setIsArchiveModalOpen(true);
                                                }}
                                                href="#"
                                                className="button-left-icon w-inline-block"
                                            >
                                                <div className="button-icon-left"></div>
                                                <div className="text-block-4">{data.archiveButton.text}</div>
                                            </a>
                                        )} */}
                                        <SubscriberLink
                                            href={data.downloadButton.link}
                                            target="_blank"
                                            className="button-left-icon w-inline-block"
                                        >
                                            <div className="button-icon-left"></div>
                                            <div className="text-block-4">{data.downloadButton.text}</div>
                                        </SubscriberLink>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        id={data.id}
                        data-w-id="315b066c-9375-5d43-64fe-99c0825fb648"
                        className={data.className}
                        style={{ willChange: 'opacity, transform', opacity: 1, transform: 'translate3d(0px, -40px, 0px)' }}

                    >
                        <img
                            ref={imageRef}
                            src={data.image.src}
                            loading="lazy"
                            sizes="(max-width: 991px) 90vw, 480px"
                            srcSet={`${data.image.src} 500w, ${data.image.src} 800w, ${data.image.src} 1080w, ${data.image.src} 1600w`}
                            alt={data.image.alt}
                            className="cover-visual insights"
                            style={{ willChange: 'transform', transform: 'translate3d(0px, 0px, 0px) scale(1)' }}
                        />
                    </div>
                </div>
            </section>
            {data.archivePlans && (
                <ArchivePlansModal
                    isOpen={isArchiveModalOpen}
                    onClose={() => setIsArchiveModalOpen(false)}
                    plans={data.archivePlans}
                    title={data.archiveButton?.text}
                    tableHeaders={data.archiveButton?.tableHeaders}
                />
            )}
        </div>
    );
};

export default InsightsHero;
